/* eslint-disable react/no-unescaped-entities */
/**
 * Landing A — Éditoriale premium.
 *
 * Ton : lecture ample, typographie dominante, photos d'architecture,
 * mise en page bento asymétrique. Cible fondateurs, gérants SCI, patrimoine
 * établi.
 */
import React, { useState } from "react";
import { BRAND } from "@/config/brand";
import { Btn, Chip, Section, Card, H1, Money, C_TOKENS as C } from "./primitives";
import { FoundationHorizontal, FoundationSymbol } from "./logos";
import BetaFormDialog from "./BetaFormDialog";

const PILLARS = [
  { title: "Patrimoine", copy: "SCI, immeubles, lots et documents.", icon: "P" },
  { title: "Gestion locative", copy: "Locataires, baux, loyers, quittances et relances.", icon: "L" },
  { title: "Suivi financier", copy: "Charges, crédits, rentabilité et cash-flow.", icon: "F" },
  { title: "Organisation", copy: "Documents, alertes, échéances et exports comptables.", icon: "O" },
];

const AUDIENCES = [
  {
    tag: "Propriétaire bailleur",
    lead: "Suivez vos loyers, quittances et charges sans tableur.",
    detail: "Loyers attendus, encaissés, en retard : un chiffre par ligne, jamais deux clics de plus. Génération automatique des quittances et relances.",
  },
  {
    tag: "Gérant de SCI",
    lead: "Consolidez plusieurs sociétés dans une même vue.",
    detail: "Multi-SCI, participations, flux intragroupes, comptes courants d'associés — visibles ensemble, exports comptables préparés.",
  },
  {
    tag: "Investisseur multi-lots",
    lead: "Comparez rentabilité et cash-flow par immeuble.",
    detail: "Rendement brut, net, net-net après crédit et charges — sur tout le portefeuille, sur un lot, sur un projet.",
  },
  {
    tag: "Marchand de biens",
    lead: "Structurer vos opérations d'achat-revente.",
    detail: "Coût total, marge brute, trésorerie nécessaire, timeline par étapes. Disponible en bêta.",
    beta: true,
  },
];

const FEATURES = [
  { label: "Gestion multi-patrimoines", state: "Disponible" },
  { label: "Gestion des SCI et participations", state: "Disponible" },
  { label: "Immeubles et lots", state: "Disponible" },
  { label: "Locataires et baux", state: "Disponible" },
  { label: "Échéances et loyers", state: "Disponible" },
  { label: "Quittances automatiques", state: "Disponible" },
  { label: "Charges et répartitions", state: "Disponible" },
  { label: "Crédits et amortissements", state: "Disponible" },
  { label: "Cash-flow consolidé", state: "Disponible" },
  { label: "États des lieux mobiles", state: "Disponible" },
  { label: "Coffre documentaire", state: "Disponible" },
  { label: "Espace locataire", state: "Bêta" },
  { label: "Exports comptables", state: "Bêta" },
  { label: "OCR et automatisations", state: "Bientôt" },
];

const STEPS = [
  ["1", "Ajoutez votre patrimoine", "Créez vos SCI ou détentions personnelles en quelques minutes."],
  ["2", "Renseignez vos immeubles et lots", "Adresse, surface, tantièmes, statut d'occupation."],
  ["3", "Créez vos baux et locataires", "Bail vide, meublé, commercial, garage — génération d'échéances."],
  ["4", "Suivez vos loyers et votre rentabilité", "Encaissements, retards, cash-flow, exports comptables."],
];

const FAQ = [
  ["À qui s'adresse Fonbex ?", "Aux propriétaires bailleurs, investisseurs, gérants de SCI et futurs marchands de biens qui veulent centraliser leur gestion locative et patrimoniale."],
  ["Puis-je gérer plusieurs SCI ?", "Oui. Fonbex est conçu multi-sociétés : chaque SCI, holding ou détention personnelle est isolée et consolidable."],
  ["Puis-je importer mes documents ?", "Oui. Baux, quittances, factures, diagnostics : tout se dépose dans le coffre documentaire, avec rattachement aux immeubles, lots et locataires."],
  ["Fonbex remplace-t-il un expert-comptable ?", "Non. Fonbex prépare les données (exports par catégorie, TVA, écritures) pour un envoi net à votre expert-comptable."],
  ["Mes locataires disposent-ils d'un espace personnel ?", "Oui. Un accès sécurisé par lien magique permet à chaque locataire de consulter bail, quittances, documents et de signaler une intervention."],
  ["L'application est-elle accessible sur mobile ?", "Oui, tous les écrans sont responsive. Les états des lieux sont pensés pour la saisie sur smartphone."],
  ["Combien coûte Fonbex ?", "L'accès est gratuit pendant la phase bêta, avec un tarif privilégié à l'ouverture commerciale."],
  ["Comment rejoindre la bêta ?", "En remplissant le formulaire ci-dessus. Nous revenons vers vous sous quelques jours."],
];

const StateBadge = ({ state }) => {
  const map = {
    "Disponible": { tone: "success", label: "Disponible" },
    "Bêta": { tone: "info", label: "Bêta" },
    "Bientôt": { tone: "neutral", label: "Bientôt" },
  };
  return <Chip tone={map[state].tone}>{map[state].label}</Chip>;
};

export default function LandingA() {
  const [betaOpen, setBetaOpen] = useState(false);
  return (
    <div data-testid="fonbex-landing-a">
      {/* HERO ------------------------------------------------------------------- */}
      <section style={{ backgroundColor: C.white, padding: "80px 0 120px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 40 }}>
            <FoundationHorizontal height={40} tone="dark" />
            <Chip tone="info">Bêta privée — accès sur invitation</Chip>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 64, alignItems: "center" }}
               className="fonbex-hero-grid">
            <div>
              <H1>
                Gérez, suivez et valorisez
                <br />
                <span style={{ color: C.primary }}>votre immobilier.</span>
              </H1>
              <p style={{
                fontSize: 20, lineHeight: 1.55, color: C.textSecondary,
                marginTop: 24, marginBottom: 36, maxWidth: 560,
              }}>
                {BRAND.name} centralise vos biens, SCI, locataires, baux, loyers,
                charges, crédits et documents dans une interface claire et
                professionnelle.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Btn size="lg" onClick={() => setBetaOpen(true)}
                     testid="fonbex-hero-cta-beta">
                  Demander un accès bêta
                </Btn>
                <Btn size="lg" variant="secondary"
                     onClick={() => window.location.href = "#features"}
                     testid="fonbex-hero-cta-discover">
                  Découvrir {BRAND.name}
                </Btn>
              </div>
              <div style={{
                marginTop: 40, display: "flex", gap: 24, flexWrap: "wrap",
                color: C.textSecondary, fontSize: 13,
              }}>
                <span>✓ Sans engagement</span>
                <span>✓ Vos données restent isolées</span>
                <span>✓ Support en français</span>
              </div>
            </div>
            <HeroMosaic />
          </div>
        </div>
      </section>

      {/* PROMESSE 4 PILIERS ---------------------------------------------------- */}
      <Section
        eyebrow="Promesse"
        title="Toute votre gestion immobilière au même endroit."
        subtitle="Quatre piliers cohérents, une source unique de vérité. Fini les tableurs éparpillés et les documents perdus."
        background={C.surface}
        testid="fonbex-section-pillars"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20,
        }}>
          {PILLARS.map((p) => (
            <Card key={p.title} hoverable testid={`fonbex-pillar-${p.title}`}>
              <div style={{
                width: 44, height: 44, borderRadius: 6,
                backgroundColor: C.lightBlue, color: C.primary,
                display: "grid", placeItems: "center",
                fontWeight: 700, fontSize: 18, marginBottom: 16,
                fontFamily: "'Cabinet Grotesk', sans-serif",
              }}>{p.icon}</div>
              <div style={{ fontWeight: 700, color: C.night, fontSize: 20, marginBottom: 6 }}>
                {p.title}
              </div>
              <div style={{ color: C.textSecondary, fontSize: 15, lineHeight: 1.5 }}>
                {p.copy}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* FONCTIONS ------------------------------------------------------------- */}
      <Section
        id="features"
        eyebrow="Fonctions"
        title="14 modules, un seul produit."
        subtitle="Chaque fonction annoncée est classée par disponibilité. Aucune fausse promesse."
        testid="fonbex-section-features"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
        }}>
          {FEATURES.map((f) => (
            <div key={f.label}
                 style={{
                   padding: "14px 18px", borderRadius: 6,
                   border: `1px solid ${C.border}`, backgroundColor: C.white,
                   display: "flex", alignItems: "center", justifyContent: "space-between",
                 }}
                 data-testid={`fonbex-feature-${f.label}`}>
              <span style={{ color: C.night, fontWeight: 500, fontSize: 15 }}>{f.label}</span>
              <StateBadge state={f.state} />
            </div>
          ))}
        </div>
      </Section>

      {/* AUDIENCES ------------------------------------------------------------- */}
      <Section
        eyebrow="Publics concernés"
        title="Quatre profils, un seul outil qui s'adapte."
        background={C.surface}
        testid="fonbex-section-audiences"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20,
        }}>
          {AUDIENCES.map((a) => (
            <Card key={a.tag} hoverable>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Chip tone={a.beta ? "warning" : "info"}>{a.tag}</Chip>
                {a.beta && <Chip tone="neutral">à venir</Chip>}
              </div>
              <div style={{ fontWeight: 700, fontSize: 20, color: C.night, marginBottom: 10 }}>
                {a.lead}
              </div>
              <p style={{ color: C.textSecondary, lineHeight: 1.55, fontSize: 15 }}>
                {a.detail}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* PARCOURS -------------------------------------------------------------- */}
      <Section
        eyebrow="Démarrage"
        title="Un parcours simple, en quatre étapes."
        testid="fonbex-section-steps"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
        }}>
          {STEPS.map(([n, title, sub]) => (
            <div key={n} style={{ position: "relative" }}>
              <div style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: 56, fontWeight: 700, color: C.lightBlue, lineHeight: 1,
                marginBottom: -14,
              }}>{n}</div>
              <div style={{ paddingLeft: 4 }}>
                <div style={{ fontWeight: 700, color: C.night, fontSize: 18, marginBottom: 8 }}>
                  {title}
                </div>
                <div style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.55 }}>
                  {sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* DASHBOARD PREVIEW ---------------------------------------------------- */}
      <Section
        eyebrow="Tableau de bord"
        title="Vos indicateurs financiers en un coup d'œil."
        subtitle="Loyers attendus, encaissés, charges, crédit, cash-flow, taux d'occupation, alertes prioritaires : lisibles d'un seul regard."
        background={C.night}
        testid="fonbex-section-dashboard"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16,
        }}>
          <KpiCardDark label="Revenus locatifs (30j)" value="18 420" />
          <KpiCardDark label="Charges" value="4 210" negative />
          <KpiCardDark label="Mensualités crédit" value="7 850" negative />
          <KpiCardDark label="Cash-flow net" value="+ 6 360" positive />
          <KpiCardDark label="Taux d'occupation" value="94 %" suffix="" />
          <KpiCardDark label="Alertes prioritaires" value="3" tone="alert" />
        </div>
      </Section>

      {/* TENANT ---------------------------------------------------------------- */}
      <Section
        eyebrow="Espace locataire"
        title="Un accès simple, sécurisé, sans compte à créer."
        subtitle="Vos locataires accèdent à leur bail, à leurs quittances et à leurs documents par un lien magique temporaire. Aucun mot de passe à retenir."
        testid="fonbex-section-tenant"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32, alignItems: "center",
        }} className="fonbex-tenant-grid">
          <ul style={{ listStyle: "none", padding: 0, display: "grid", gap: 16 }}>
            {[
              ["Accès par lien magique sécurisé", "Expire automatiquement. Rien à mémoriser."],
              ["Consultation du bail", "Version signée et versions successives."],
              ["Quittances", "Téléchargement PDF horodaté."],
              ["Demandes d'intervention", "Photos, priorité, statut de traitement."],
              ["Échanges simplifiés", "Messagerie liée au dossier."],
            ].map(([t, sub]) => (
              <li key={t} style={{ display: "flex", gap: 12 }}>
                <div style={{
                  minWidth: 24, height: 24, borderRadius: 12, backgroundColor: C.lightBlue,
                  color: C.primary, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12,
                }}>✓</div>
                <div>
                  <div style={{ fontWeight: 600, color: C.night }}>{t}</div>
                  <div style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.5 }}>{sub}</div>
                </div>
              </li>
            ))}
          </ul>
          <TenantMockup />
        </div>
      </Section>

      {/* SECURITY -------------------------------------------------------------- */}
      <Section
        eyebrow="Confidentialité"
        title="Vos données restent confidentielles."
        subtitle="Fonbex intègre la protection des données dès sa conception : accès sécurisés, séparation des comptes, liens temporaires et contrôle des autorisations. Les documents juridiques et les procédures de conformité sont finalisés avant l'ouverture commerciale."
        background={C.surface}
        testid="fonbex-section-security"
      >
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20,
        }}>
          {[
            ["Isolation stricte", "Chaque compte a sa propre organisation. Aucune donnée n'est visible entre comptes."],
            ["Accès sécurisés", "Cookies httpOnly, protection contre les tentatives répétées, sessions révocables."],
            ["Liens magiques temporaires", "Les invitations locataires expirent automatiquement et peuvent être révoquées."],
            ["Gestion des rôles", "7 rôles applicatifs (owner, admin, gestionnaire, comptable, lecture seule, locataire)."],
          ].map(([t, sub]) => (
            <div key={t}>
              <div style={{ fontWeight: 700, color: C.night, marginBottom: 8 }}>{t}</div>
              <div style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.55 }}>{sub}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* BETA CTA -------------------------------------------------------------- */}
      <Section
        eyebrow="Bêta privée"
        title="Participez aux premiers essais de Fonbex."
        subtitle="Fonbex ouvre progressivement son accès à un groupe limité de propriétaires, investisseurs et gérants de SCI."
        background={C.night}
        testid="fonbex-section-beta"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 40, alignItems: "start" }}
             className="fonbex-beta-grid">
          <div>
            <ul style={{ listStyle: "none", padding: 0, display: "grid", gap: 14 }}>
              {[
                "Accès gratuit pendant la phase bêta",
                "Accompagnement personnalisé",
                "Remontée prioritaire de vos besoins",
                "Tarif bêta privilégié après le lancement",
              ].map((t) => (
                <li key={t} style={{ display: "flex", alignItems: "center", gap: 12, color: C.white, fontSize: 16 }}>
                  <span style={{ color: C.primary }}>—</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <Card style={{ backgroundColor: C.white, padding: 32 }}>
            <div style={{ fontWeight: 700, fontSize: 22, color: C.night, marginBottom: 8 }}>
              Formulaire d'accès
            </div>
            <div style={{ color: C.textSecondary, marginBottom: 20, fontSize: 14 }}>
              Sept champs, deux minutes. Vous recevez une réponse sous cinq jours ouvrés.
            </div>
            <Btn size="lg" onClick={() => setBetaOpen(true)}
                 testid="fonbex-beta-cta"
                 style={{ width: "100%" }}>
              Demander mon accès bêta
            </Btn>
          </Card>
        </div>
      </Section>

      {/* FAQ ------------------------------------------------------------------- */}
      <Section
        eyebrow="FAQ"
        title="Vos questions, nos réponses."
        testid="fonbex-section-faq"
      >
        <div style={{ display: "grid", gap: 12 }}>
          {FAQ.map(([q, a]) => (
            <details key={q}
                     data-testid={`fonbex-faq-${q.substring(0, 12)}`}
                     style={{
                       backgroundColor: C.white, border: `1px solid ${C.border}`,
                       borderRadius: 8, padding: "18px 22px",
                     }}>
              <summary style={{
                cursor: "pointer", fontWeight: 600, color: C.night, fontSize: 16,
              }}>{q}</summary>
              <p style={{ marginTop: 12, color: C.textSecondary, fontSize: 15, lineHeight: 1.6 }}>
                {a}
              </p>
            </details>
          ))}
        </div>
      </Section>

      <BetaFormDialog open={betaOpen} onOpenChange={setBetaOpen} />

      {/* Reset heavy grids on small viewports */}
      <style>{`
        @media (max-width: 900px) {
          .fonbex-hero-grid, .fonbex-tenant-grid, .fonbex-beta-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

const KpiCardDark = ({ label, value, positive, negative, suffix = "€", tone }) => (
  <div style={{
    borderRadius: 8, padding: 20, backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
  }}>
    <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: "0.05em",
                  textTransform: "uppercase", fontWeight: 600 }}>
      {label}
    </div>
    <div style={{
      color: tone === "alert" ? C.accent : (positive ? "#7EE2A8" : (negative ? "#FFB4BA" : C.white)),
      fontFamily: "'IBM Plex Sans'", fontVariantNumeric: "tabular-nums",
      fontSize: 30, fontWeight: 700, marginTop: 6, letterSpacing: "-0.02em",
    }}>
      {value} {suffix}
    </div>
  </div>
);

const HeroMosaic = () => (
  <div style={{
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, position: "relative",
  }}>
    <Card style={{ padding: 20, gridRow: "span 2" }}>
      <Chip tone="info">Tableau de bord</Chip>
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 13, color: C.textSecondary }}>Cash-flow net (30 jours)</div>
        <div style={{ fontFamily: "'IBM Plex Sans'", fontSize: 34, fontWeight: 700,
                      color: C.night, marginTop: 4, letterSpacing: "-0.02em",
                      fontVariantNumeric: "tabular-nums" }}>
          <Money value={6360} positive />
        </div>
        <div style={{ marginTop: 16, height: 90, position: "relative" }}>
          <svg viewBox="0 0 300 90" width="100%" height="100%">
            <path d="M0,70 C40,60 60,50 100,52 C140,54 160,30 200,28 C240,26 260,18 300,14"
                  stroke={C.primary} strokeWidth="2.5" fill="none" />
            <path d="M0,70 C40,60 60,50 100,52 C140,54 160,30 200,28 C240,26 260,18 300,14 L300,90 L0,90 Z"
                  fill={C.lightBlue} opacity="0.9" />
          </svg>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10,
                      fontSize: 11, color: C.textSecondary }}>
          <span>Jan</span><span>Fév</span><span>Mar</span><span>Avr</span><span>Mai</span>
        </div>
      </div>
    </Card>
    <Card style={{ padding: 18 }}>
      <div style={{ fontSize: 12, color: C.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.1em" }}>Loyers du mois</div>
      <div style={{ fontFamily: "'IBM Plex Sans'", fontSize: 24, fontWeight: 700,
                    marginTop: 6, color: C.night, fontVariantNumeric: "tabular-nums" }}>
        18 420 €
      </div>
      <div style={{ marginTop: 8 }}>
        <Chip tone="success">17 / 18 encaissés</Chip>
      </div>
    </Card>
    <Card style={{ padding: 18 }}>
      <div style={{ fontSize: 12, color: C.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.1em" }}>Alertes</div>
      <div style={{ fontFamily: "'IBM Plex Sans'", fontSize: 24, fontWeight: 700,
                    marginTop: 6, color: C.accent, fontVariantNumeric: "tabular-nums" }}>
        3
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: C.textSecondary }}>
        1 impayé · 1 assurance · 1 doc manquant
      </div>
    </Card>
  </div>
);

const TenantMockup = () => (
  <div style={{
    borderRadius: 24, backgroundColor: C.night, padding: 12, boxShadow: "0 20px 60px -12px rgba(16,42,86,0.25)",
    maxWidth: 320, margin: "0 auto",
  }}>
    <div style={{ backgroundColor: C.white, borderRadius: 18, overflow: "hidden" }}>
      <div style={{ backgroundColor: C.primary, padding: "16px 20px", color: C.white }}>
        <div style={{ fontSize: 12, opacity: 0.8 }}>{BRAND.tenantSpaceName}</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Bonjour, Camille</div>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 12, color: C.textSecondary, textTransform: "uppercase" }}>
          Prochain loyer
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.night,
                      marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
          1 250 € · 5 mars
        </div>
        <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 12, color: C.textSecondary, textTransform: "uppercase" }}>
            Dernière quittance
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ color: C.night, fontWeight: 500 }}>Février 2026</span>
            <Chip tone="success">Payée</Chip>
          </div>
        </div>
      </div>
    </div>
  </div>
);
